import type { ObservabilityOptions } from './declarations/Observability';
import { globalObservability } from './global';
import { defaultInitDatadogRumClient } from './internal/builtinDatadog';
import { defaultInitSentry } from './internal/builtinSentry';
import { buildDefaultDatadogOptions } from './internal/datadog';
import { getDeployEnv } from './internal/deployEnv';
import { loadGoogleTagManager } from './internal/googleTagManager';
import { BASE_JOURNEY_ENDPOINT, initJourney } from './internal/journey';
import { loadSegment } from './internal/segment';
import { buildDefaultSentryOpts } from './internal/sentry';
import { DebugObservability, RealObservability } from './observability-base';

/** Call this before you use useObservability or globalObservability. */
export function initObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
>({
  appName,
  env,
  version,
  eventTrackingCategory,
  sentry,
  datadog,
  debug,
  sendJourneyEventsInDev,
  journeyEndpoint,
  isInternalTool,
  isBot,
}: ObservabilityOptions<AppCategory>): void {
  if (globalObservability.isInitialized()) {
    throw new Error('initObservability was already called');
  }

  env = env || getDeployEnv();
  if (env === 'development') {
    let journeyEventClient: ReturnType<typeof initJourney>['eventClient'];
    if (sendJourneyEventsInDev) {
      // manually set the journey endpoint to staging when in dev mode
      const { eventClient } = initJourney(BASE_JOURNEY_ENDPOINT);
      journeyEventClient = eventClient;
    }
    globalObservability.setCurrentInstance(
      new DebugObservability<AppCategory, AppActions>(
        { log: debug, sendJourneyEventsInDev, journeyEventClient, env },
        eventTrackingCategory,
      ),
    );
  } else {
    // TODO: Hide these in observability/internal like initSentry.
    if (isInternalTool !== true && !isBot) {
      // Load Google Tag Manager only for non-internal, non-bot traffic.
      loadGoogleTagManager();
    }
    if (!isBot) {
      loadSegment();
    }

    const realObservabilityInstance = new RealObservability<AppCategory, AppActions>(
      eventTrackingCategory,
    );
    if (!isBot) {
      const { eventClient, userClient } = initJourney(journeyEndpoint);
      userClient && realObservabilityInstance.setJourneyUserClient(userClient);
      eventClient && realObservabilityInstance.setJourneyEventClient(eventClient);
    }
    realObservabilityInstance.setSentryClient(
      defaultInitSentry({ ...buildDefaultSentryOpts({ appName, env, version }), ...sentry }),
    );
    realObservabilityInstance.setDatadogRumClient(
      defaultInitDatadogRumClient({
        ...buildDefaultDatadogOptions({ appName, env, version }),
        ...datadog,
      }),
    );
    globalObservability.setCurrentInstance(realObservabilityInstance);
  }
}

export * from './contextualData';
export type {
  CategoryName,
  DatadogContext,
  DatadogDefaultPrivacyLevel,
  DatadogOptions,
  DatadogPropagatorType,
  DatadogRumClientInstance,
  DatadogTracingMatchOption,
  DatadogTracingUrl,
  DeployEnv,
  EventData,
  EventName,
  EventTrackingAction,
  EventTrackingCategory,
  IEventCategoriesActionsMap,
  JourneyClient,
  JourneyClientInstance,
  JourneyEventWrapper,
  Observability,
  ObservabilityBaseOptions,
  ObservabilityOptions,
  ObservabilitySlimOptions,
  SentryClientInstance,
  SentryEvent,
  SentryOptions,
  SentryScope,
  TrackJourneyEventOptions,
  TrackingOptions,
  UseObservabilityTrackJourneyEvent,
  UserEvent,
} from './declarations';
export * from './react/ContextualDataContext';
export * from './react/useObservability';
export { Sentry, loadSentry } from './internal/builtinSentry';
export { globalObservability } from './global';
export * from './constants';
export * from './observability-base';
export { analytics } from './analytics';
export { setRUMUser, setRUMUserProperty } from './internal/datadog';
export { subscribeToDevtoolsEvents, type DevtoolsEvent } from './devtools-events';
